import type { ConfigurationSetting, ConfigurationSettingChange } from "@/resources/configuration-settings/configuration-settings.api";
import type {
  ScoringWeights,
  RelevanceConfig,
  TrendTier,
  PenaltySettings,
  KeywordSalesTier,
  HeroRankTier,
  ChangeEdit,
  ChangeLogEntry,
  SampleKeywordPreset,
  FactorKey,
  SerpMatchConfig,
} from "./types";
import {
  ALIAS_META,
  defaultWeights,
  defaultRelevance,
  defaultTrendTiers,
  defaultPenalties,
  defaultAsinDensityMax,
  defaultKeywordSalesMax,
  defaultCompetitionMax,
  defaultFactorEnabled,
  defaultKeywordSalesTiers,
  defaultHeroRankTiers,
} from "./constants";

export function formatAliasFallback(alias: string): string {
  return alias
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatChangeValue(alias: string, value: string | null): string {
  if (value === null || value === "") return "—";
  // AUT-361: the seven `kps_*_enabled` toggles are stored in `configuration_settings.value` as the
  // strings "1" and "0" (seeded that way in `08_seed_configuration_settings.sql`, read back as
  // `(string) $raw !== '0'`), so the change history rendered a factor being switched off as
  // "1 -> 0". Every other alias in ALIAS_META is numeric or the comma-list below, which makes the
  // `_enabled` suffix an exact discriminator -- and one a newly added factor toggle inherits for
  // free. Anything other than "1"/"0" falls through to the raw value rather than guessing.
  if (alias.endsWith("_enabled")) {
    if (value === "1") return "On";
    if (value === "0") return "Off";
  }
  if (alias === "hero_embeddings_components") {
    return value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(", ");
  }
  return value;
}

export function keywordSalesTierPoints(keywordSales: number, tiers: KeywordSalesTier[]): number {
  if (keywordSales < 5000) return tiers[0]?.points ?? 0;
  if (keywordSales < 8000) return tiers[1]?.points ?? 0;
  if (keywordSales < 12000) return tiers[2]?.points ?? 0;
  if (keywordSales < 25000) return tiers[3]?.points ?? 0;
  return tiers[4]?.points ?? 0;
}

export function heroRankTierPoints(heroRank: number | null, tiers: HeroRankTier[]): number {
  if (heroRank === null) return tiers[0]?.points ?? 0;
  if (heroRank <= 10) return tiers[1]?.points ?? 0;
  if (heroRank <= 30) return tiers[2]?.points ?? 0;
  if (heroRank <= 100) return tiers[3]?.points ?? 0;
  return tiers[4]?.points ?? 0;
}

export function searchTrendTierPoints(searchTrend: number, tiers: TrendTier[]): number {
  for (let i = 0; i < tiers.length - 1; i++) {
    if (searchTrend <= tiers[i].maxBound) return tiers[i].points;
  }
  return tiers[tiers.length - 1]?.points ?? 0;
}

/**
 * Competition scoring — a log-normal bell curve peaking at 10,000 competitors.
 *   d     = log10(competitors) − log10(10000)
 *   sigma = 0.85 when d < 0 (thin competition falls off slowly), else 0.41
 *   score = max × e^(−d² / (2 × sigma²))
 * Exactly mirrors the backend SQL. Too few competitors means thin demand;
 * too many means the keyword is saturated.
 */
export function competitionScore(competitors: number, max: number) {
  if (!competitors || competitors <= 0) return 0;
  const d = Math.log10(competitors) - Math.log10(10000);
  const sigma = d < 0 ? 0.85 : 0.41;
  return max * Math.exp(-(d * d) / (2 * sigma * sigma));
}

export function calcSampleKPS(
  weights: ScoringWeights,
  sample: SampleKeywordPreset,
  asinDensityMax: number,
  keywordSalesTiers: KeywordSalesTier[],
  heroRankTiers: HeroRankTier[],
  trendTiers: TrendTier[],
  competitionMax: number,
  factorEnabled: Record<FactorKey, boolean>,
) {
  const svScore = factorEnabled.searchVolume ? (Math.min(sample.searchVolume, 10000) / 10000) * weights.searchVolume : 0;
  const hrScore = factorEnabled.heroRank ? heroRankTierPoints(sample.heroRank, heroRankTiers) : 0;
  const relScore = factorEnabled.relevance
    ? (sample.relevance >= 100 ? weights.relevance : (sample.relevance / 100) * weights.relevance)
    : 0;
  const svtScore = factorEnabled.searchTrend ? searchTrendTierPoints(sample.searchTrend, trendTiers) : 0;
  const adScore = factorEnabled.asinDensity ? (Math.min(sample.asinDensity, 100) / 100) * asinDensityMax : 0;
  const ksScore = factorEnabled.keywordSales ? keywordSalesTierPoints(sample.keywordSales, keywordSalesTiers) : 0;
  const compScore = factorEnabled.competition ? competitionScore(sample.competitors, competitionMax) : 0;
  // AUT-200: renormalize over the ACTIVE factors, mirroring
  // 06_calc_kps_for_keywords.sql. The stored procedure divides the summed
  // sub-scores by the sum of the maxes of factors that are switched on, so
  // KPS stays on a 0-100 scale when a factor is deactivated. Summing the
  // points alone would make the preview fall as factors are turned off --
  // the opposite direction to the real score, which is precisely the
  // comparison this card exists to support.
  const activeMax =
    (factorEnabled.searchVolume ? weights.searchVolume : 0) +
    (factorEnabled.heroRank ? Math.max(0, ...heroRankTiers.map((t) => t.points)) : 0) +
    (factorEnabled.relevance ? weights.relevance : 0) +
    (factorEnabled.searchTrend ? Math.max(0, ...trendTiers.map((t) => t.points)) : 0) +
    (factorEnabled.asinDensity ? asinDensityMax : 0) +
    (factorEnabled.keywordSales ? Math.max(0, ...keywordSalesTiers.map((t) => t.points)) : 0) +
    (factorEnabled.competition ? competitionMax : 0);

  const summed = svScore + hrScore + relScore + svtScore + adScore + ksScore + compScore;
  const total = activeMax > 0 ? Math.min(100, Math.floor((summed * 100) / activeMax)) : 0;
  return {
    total,
    svScore: Math.round(svScore * 10) / 10,
    hrScore: Math.round(hrScore * 10) / 10,
    relScore: Math.round(relScore * 10) / 10,
    svtScore: Math.round(svtScore * 10) / 10,
    adScore: Math.round(adScore * 10) / 10,
    ksScore: Math.round(ksScore * 10) / 10,
    compScore: Math.round(compScore * 10) / 10,
  };
}

export function settingsToMap(settings: ConfigurationSetting[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const s of settings) {
    map[s.alias] = s.value;
  }
  return map;
}

export function applySettingsToState(m: Record<string, string>) {
  const n = (key: string, fallback: number) => {
    const v = m[key];
    if (v === undefined) return fallback;
    const parsed = parseFloat(v);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const weights: ScoringWeights = {
    searchVolume: n("kps_search_score_max", defaultWeights.searchVolume),
    heroRank: n("kps_hero_rank_score_max", defaultWeights.heroRank),
    relevance: n("kps_hero_relevance_score_max", defaultWeights.relevance),
    searchVolumeTrend: n("kps_search_trend_score_max", defaultWeights.searchVolumeTrend),
  };

  const asinDensityMax = n("kps_asin_density_score_max", defaultAsinDensityMax);
  const keywordSalesMax = n("kps_keyword_sales_score_max", defaultKeywordSalesMax);
  const competitionMax = n("kps_competition_score_max", defaultCompetitionMax);

  const b = (key: string, fallback: boolean) => {
    const v = m[key];
    if (v === undefined) return fallback;
    return v === "1" || v === "true";
  };
  const factorEnabled: Record<FactorKey, boolean> = {
    relevance: b("kps_relevance_enabled", defaultFactorEnabled.relevance),
    searchVolume: b("kps_search_volume_enabled", defaultFactorEnabled.searchVolume),
    heroRank: b("kps_hero_rank_enabled", defaultFactorEnabled.heroRank),
    asinDensity: b("kps_asin_density_enabled", defaultFactorEnabled.asinDensity),
    keywordSales: b("kps_keyword_sales_enabled", defaultFactorEnabled.keywordSales),
    searchTrend: b("kps_search_trend_enabled", defaultFactorEnabled.searchTrend),
    competition: b("kps_competition_enabled", defaultFactorEnabled.competition),
  };

  const keywordSalesTiers: KeywordSalesTier[] = [
    { ...defaultKeywordSalesTiers[0], points: n("kps_keyword_sales_tier1_points", defaultKeywordSalesTiers[0].points) },
    { ...defaultKeywordSalesTiers[1], points: n("kps_keyword_sales_tier2_points", defaultKeywordSalesTiers[1].points) },
    { ...defaultKeywordSalesTiers[2], points: n("kps_keyword_sales_tier3_points", defaultKeywordSalesTiers[2].points) },
    { ...defaultKeywordSalesTiers[3], points: n("kps_keyword_sales_tier4_points", defaultKeywordSalesTiers[3].points) },
    { ...defaultKeywordSalesTiers[4], points: n("kps_keyword_sales_tier5_points", defaultKeywordSalesTiers[4].points) },
  ];

  const heroRankTiers: HeroRankTier[] = [
    { ...defaultHeroRankTiers[0], points: n("kps_hero_rank_tier1_points", defaultHeroRankTiers[0].points) },
    { ...defaultHeroRankTiers[1], points: n("kps_hero_rank_tier2_points", defaultHeroRankTiers[1].points) },
    { ...defaultHeroRankTiers[2], points: n("kps_hero_rank_tier3_points", defaultHeroRankTiers[2].points) },
    { ...defaultHeroRankTiers[3], points: n("kps_hero_rank_tier4_points", defaultHeroRankTiers[3].points) },
    { ...defaultHeroRankTiers[4], points: n("kps_hero_rank_tier5_points", defaultHeroRankTiers[4].points) },
  ];

  const trendTiers: TrendTier[] = [
    { label: "Tier 1", maxBound: n("kps_search_trend_score_tier1_max_bound", defaultTrendTiers[0].maxBound), points: n("kps_search_trend_score_tier1_points", defaultTrendTiers[0].points) },
    { label: "Tier 2", maxBound: n("kps_search_trend_score_tier2_max_bound", defaultTrendTiers[1].maxBound), points: n("kps_search_trend_score_tier2_points", defaultTrendTiers[1].points) },
    { label: "Tier 3", maxBound: n("kps_search_trend_score_tier3_max_bound", defaultTrendTiers[2].maxBound), points: n("kps_search_trend_score_tier3_points", defaultTrendTiers[2].points) },
    { label: "Tier 4", maxBound: n("kps_search_trend_score_tier4_max_bound", defaultTrendTiers[3].maxBound), points: n("kps_search_trend_score_tier4_points", defaultTrendTiers[3].points) },
    { label: "Tier 5", maxBound: n("kps_search_trend_score_tier5_max_bound", defaultTrendTiers[4].maxBound), points: n("kps_search_trend_score_tier5_points", defaultTrendTiers[4].points) },
  ];

  const penalties: PenaltySettings = {
    competitorBrandPenalty: n("hero_relevance_competitor_brand_penalty_multiplier", defaultPenalties.competitorBrandPenalty),
    noOverlapPenalty: n("hero_relevance_no_overlap_penalty_multiplier", defaultPenalties.noOverlapPenalty),
  };

  const heroComponentsRaw = m["hero_embeddings_components"] ?? "title,brand,bullets,features,description";
  const heroComponents = heroComponentsRaw.split(",").map((c) => {
    const t = c.trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
  });
  const relevance: RelevanceConfig = { heroComponents };
  const serpMatch: SerpMatchConfig = {
    cutoffMultiplier: n("serp_match_cutoff_multiplier", 0.8),
    anchorTopN: n("serp_match_anchor_top_n", 20),
  };

  return { weights, asinDensityMax, keywordSalesMax, competitionMax, keywordSalesTiers, heroRankTiers, trendTiers, penalties, relevance, serpMatch, factorEnabled };
}

export function stateToSettings(
  weights: ScoringWeights,
  asinDensityMax: number,
  keywordSalesMax: number,
  competitionMax: number,
  keywordSalesTiers: KeywordSalesTier[],
  heroRankTiers: HeroRankTier[],
  trendTiers: TrendTier[],
  penalties: PenaltySettings,
  relevance: RelevanceConfig,
  serpMatch: SerpMatchConfig,
  factorEnabled: Record<FactorKey, boolean>,
): { alias: string; value: string }[] {
  const bool = (v: boolean) => (v ? "1" : "0");
  return [
    { alias: "kps_search_score_max", value: String(weights.searchVolume) },
    { alias: "kps_hero_rank_score_max", value: String(weights.heroRank) },
    { alias: "kps_hero_relevance_score_max", value: String(weights.relevance) },
    { alias: "kps_search_trend_score_max", value: String(weights.searchVolumeTrend) },
    { alias: "kps_asin_density_score_max", value: String(asinDensityMax) },
    { alias: "kps_keyword_sales_score_max", value: String(keywordSalesMax) },
    { alias: "kps_competition_score_max", value: String(competitionMax) },
    { alias: "kps_relevance_enabled", value: bool(factorEnabled.relevance) },
    { alias: "kps_search_volume_enabled", value: bool(factorEnabled.searchVolume) },
    { alias: "kps_hero_rank_enabled", value: bool(factorEnabled.heroRank) },
    { alias: "kps_asin_density_enabled", value: bool(factorEnabled.asinDensity) },
    { alias: "kps_keyword_sales_enabled", value: bool(factorEnabled.keywordSales) },
    { alias: "kps_search_trend_enabled", value: bool(factorEnabled.searchTrend) },
    { alias: "kps_competition_enabled", value: bool(factorEnabled.competition) },
    { alias: "kps_keyword_sales_tier1_points", value: String(keywordSalesTiers[0].points) },
    { alias: "kps_keyword_sales_tier2_points", value: String(keywordSalesTiers[1].points) },
    { alias: "kps_keyword_sales_tier3_points", value: String(keywordSalesTiers[2].points) },
    { alias: "kps_keyword_sales_tier4_points", value: String(keywordSalesTiers[3].points) },
    { alias: "kps_keyword_sales_tier5_points", value: String(keywordSalesTiers[4].points) },
    { alias: "kps_hero_rank_tier1_points", value: String(heroRankTiers[0].points) },
    { alias: "kps_hero_rank_tier2_points", value: String(heroRankTiers[1].points) },
    { alias: "kps_hero_rank_tier3_points", value: String(heroRankTiers[2].points) },
    { alias: "kps_hero_rank_tier4_points", value: String(heroRankTiers[3].points) },
    { alias: "kps_hero_rank_tier5_points", value: String(heroRankTiers[4].points) },
    { alias: "kps_search_trend_score_tier1_max_bound", value: String(trendTiers[0].maxBound) },
    { alias: "kps_search_trend_score_tier2_max_bound", value: String(trendTiers[1].maxBound) },
    { alias: "kps_search_trend_score_tier3_max_bound", value: String(trendTiers[2].maxBound) },
    { alias: "kps_search_trend_score_tier4_max_bound", value: String(trendTiers[3].maxBound) },
    { alias: "kps_search_trend_score_tier5_max_bound", value: String(trendTiers[4].maxBound) },
    { alias: "kps_search_trend_score_tier1_points", value: String(trendTiers[0].points) },
    { alias: "kps_search_trend_score_tier2_points", value: String(trendTiers[1].points) },
    { alias: "kps_search_trend_score_tier3_points", value: String(trendTiers[2].points) },
    { alias: "kps_search_trend_score_tier4_points", value: String(trendTiers[3].points) },
    { alias: "kps_search_trend_score_tier5_points", value: String(trendTiers[4].points) },
    { alias: "hero_relevance_competitor_brand_penalty_multiplier", value: String(penalties.competitorBrandPenalty) },
    { alias: "hero_relevance_no_overlap_penalty_multiplier", value: String(penalties.noOverlapPenalty) },
    { alias: "hero_embeddings_components", value: relevance.heroComponents.map((c) => c.toLowerCase()).join(",") },
    // AUT-496. Sent with the rest, excluded from the recalculation trigger — SERP Match does not
    // feed KPS, so saving these must not offer to rescore every keyword.
    { alias: "serp_match_cutoff_multiplier", value: String(serpMatch.cutoffMultiplier) },
    { alias: "serp_match_anchor_top_n", value: String(serpMatch.anchorTopN) },
  ];
}

export function getAvatarInitials(userName: string): string {
  if (userName.includes("@")) {
    const local = userName.split("@")[0];
    const parts = local.split(".");
    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("");
  }
  return userName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? "")
    .join("");
}

export function formatChangeTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function groupChangesIntoEntries(changes: ConfigurationSettingChange[]): ChangeLogEntry[] {
  const groups: Map<string, ConfigurationSettingChange[]> = new Map();
  for (const ch of changes) {
    const key = `${ch.user_name}__${ch.created_at}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(ch);
    } else {
      groups.set(key, [ch]);
    }
  }
  const entries: ChangeLogEntry[] = [];
  let idx = 0;
  for (const [, group] of groups) {
    const first = group[0];
    const edits: ChangeEdit[] = group.map((ch) => {
      const meta = ALIAS_META[ch.alias] ?? { section: "Other", field: formatAliasFallback(ch.alias) };
      return {
        section: meta.section,
        field: meta.field,
        oldValue: formatChangeValue(ch.alias, ch.old_value),
        newValue: formatChangeValue(ch.alias, ch.new_value),
      };
    });
    let summary: string;
    if (edits.length === 1) {
      summary = `Updated ${edits[0].field}`;
    } else if (edits.length === 2) {
      summary = `Updated ${edits[0].field} and ${edits[1].field}`;
    } else {
      summary = `Updated ${edits[0].field}, ${edits[1].field} +${edits.length - 2} more`;
    }
    entries.push({
      id: String(idx++),
      timestamp: formatChangeTimestamp(first.created_at),
      user: first.user_name,
      summary,
      edits,
    });
  }
  return entries;
}

export function stripLeadingZeros(raw: string): string {
  if (raw === "") return raw;
  return raw.replace(/^(-?)0+(?=\d)/, "$1");
}
